import { Application, type Container } from 'pixi.js';
import { destroyPixiApp } from '../../core/destroyPixiApp';
import { loadUiFonts } from '../board/ui';

/** Something shown over the whole game, on the overlay canvas. */
export interface OverlayPiece extends Container {
  /** Fit the screen: called when it's shown and on every resize. */
  layout(width: number, height: number): void;
}

export interface OverlayOptions {
  /** Takes every tap and key while it's up (a board): nothing underneath
   *  hears them. Other pieces let taps through to the game. */
  modal?: boolean;
  /** Higher draws above: a toast over a board. */
  layer?: number;
}

/** A spot on the overlay that takes taps while the rest lets them through. */
export interface OverlayTapZone {
  /** Where it is, in screen pixels. */
  place(rect: { x: number; y: number; width: number; height: number }): void;
  remove(): void;
}

export interface OverlayHandle {
  /** Resolves once the piece is on screen; false if it was removed first
   *  or the canvas couldn't start. */
  readonly shown: Promise<boolean>;
  remove(): void;
}

/** Above everything the app draws, the age check and boards included. */
const Z_INDEX = 300;
/** How long the canvas stays up with nothing on it before it goes. */
const IDLE_MS = 4000;

/**
 * One transparent Pixi canvas over the whole game, for what shows over any
 * screen (producer 2026-10-14: everything in the game in Pixi): the refill
 * moment, the gem toast, the connection banner, and the boards a screen
 * other than the slot opens. It starts when the first piece arrives and
 * goes a few seconds after the last one leaves (through
 * {@link destroyPixiApp}, so the slot's own canvas underneath is never
 * harmed). Taps pass through to the game unless a modal piece is up.
 */
export class OverlayStage {
  static readonly shared = new OverlayStage();

  private app: Application | null = null;
  private host: HTMLDivElement | null = null;
  private starting: Promise<Application | null> | null = null;
  private readonly pieces = new Map<OverlayPiece, { modal: boolean; layer: number }>();
  private idle: ReturnType<typeof setTimeout> | null = null;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    // A modal board is up: the slot underneath mustn't spin on a space.
    event.stopPropagation();
  };

  /** Put `piece` over the game. The caller still owns it (and destroys it
   *  after removing it). */
  show(piece: OverlayPiece, options: OverlayOptions = {}): OverlayHandle {
    if (this.idle) clearTimeout(this.idle);
    this.idle = null;
    this.pieces.set(piece, { modal: options.modal ?? false, layer: options.layer ?? 0 });
    piece.zIndex = options.layer ?? 0;
    this.sync();
    let removed = false;
    const shown = this.start().then((app) => {
      if (!app || removed || !this.pieces.has(piece) || piece.destroyed) return false;
      app.stage.addChild(piece);
      piece.layout(app.screen.width, app.screen.height);
      return true;
    });
    return {
      shown,
      remove: () => {
        if (removed) return;
        removed = true;
        this.pieces.delete(piece);
        if (!piece.destroyed && piece.parent) piece.parent.removeChild(piece);
        this.sync();
        if (this.pieces.size === 0) this.idle = setTimeout(() => this.stop(), IDLE_MS);
      },
    };
  }

  /**
   * A spot over part of a piece that takes taps (a banner tapped to go
   * away) while the rest of the overlay lets them through: an invisible
   * button, named `label` for screen readers.
   */
  tapZone(label: string, onTap: () => void): OverlayTapZone {
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.style.cssText =
      'position:absolute;left:0;top:0;width:0;height:0;margin:0;padding:0;border:0;background:transparent;pointer-events:auto;cursor:pointer;';
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      onTap();
    });
    let host: HTMLDivElement | null = null;
    const attach = (): void => {
      if (this.host && host !== this.host) {
        host = this.host;
        host.appendChild(button);
      }
    };
    void this.start().then(attach);
    attach();
    return {
      place: (rect) => {
        attach();
        button.style.left = `${Math.round(rect.x)}px`;
        button.style.top = `${Math.round(rect.y)}px`;
        button.style.width = `${Math.round(rect.width)}px`;
        button.style.height = `${Math.round(rect.height)}px`;
      },
      remove: () => button.remove(),
    };
  }

  /** Pieces on screen now (browser tests). */
  get count(): number {
    return this.pieces.size;
  }

  /** The canvas, while there is one (browser tests read where it is). */
  get canvas(): HTMLCanvasElement | null {
    return this.app?.canvas ?? null;
  }

  private start(): Promise<Application | null> {
    if (this.starting) return this.starting;
    const host = document.createElement('div');
    host.dataset.testid = 'game-overlay';
    host.style.cssText = `position:fixed;inset:0;z-index:${Z_INDEX};pointer-events:none;`;
    document.body.appendChild(host);
    this.host = host;
    this.sync();
    const app = new Application();
    this.starting = (async () => {
      try {
        await app.init({
          resizeTo: host,
          backgroundAlpha: 0,
          antialias: true,
          resolution: window.devicePixelRatio,
          autoDensity: true,
        });
        await loadUiFonts();
      } catch (err) {
        console.error('[overlay] the canvas could not start', err);
        destroyPixiApp(app, 'overlay');
        host.remove();
        this.host = null;
        this.starting = null;
        return null;
      }
      if (this.host !== host) {
        destroyPixiApp(app, 'overlay');
        return null;
      }
      app.canvas.style.pointerEvents = 'inherit';
      app.stage.sortableChildren = true;
      host.appendChild(app.canvas);
      app.renderer.on('resize', (w: number, h: number) => {
        for (const piece of this.pieces.keys()) if (!piece.destroyed) piece.layout(w, h);
      });
      this.app = app;
      return app;
    })();
    return this.starting;
  }

  /** Taps and keys go to the overlay only while a modal piece is up. */
  private sync(): void {
    const modal = [...this.pieces.values()].some((p) => p.modal);
    if (this.host) this.host.style.pointerEvents = modal ? 'auto' : 'none';
    window.removeEventListener('keydown', this.onKey, true);
    if (modal) window.addEventListener('keydown', this.onKey, true);
  }

  private stop(): void {
    this.idle = null;
    if (this.pieces.size > 0) return;
    const app = this.app;
    const host = this.host;
    const starting = this.starting;
    this.app = null;
    this.host = null;
    this.starting = null;
    window.removeEventListener('keydown', this.onKey, true);
    if (app) destroyPixiApp(app, 'overlay');
    else void starting?.then((a) => a && destroyPixiApp(a, 'overlay'));
    host?.remove();
  }
}
